'use client';
import { useRef, useState, lazy, Suspense, useCallback } from 'react';
import { Icons } from '@/components/icons';

import { cn } from '@/lib/cn';
import useResizeObserver from 'use-resize-observer';
import { useReadGlobalFilter } from '../hooks/use-global-filter';
import { useQuery } from '@tanstack/react-query';
import { getGlobalFilterItems } from '../utils';
import { Badge } from '@/components/ui/badge';
import { GLOBAL_FILTER_KEY } from '../constants';
import type { GlobalFilterItem } from '../types';

const GlobalFilterModal = lazy(() =>
  import('./global-filter-modal').then((mod) => ({
    default: mod.GlobalFilterModal,
  }))
);

export function GlobalFilterSelector() {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [maxNameWidth, setMaxNameWidth] = useState(0);

  const globalFilter = useReadGlobalFilter();

  const calculateNameWidth = useCallback(() => {
    const totalWidth = btnRef.current?.offsetWidth ?? 0;
    const padding = 50;
    const nameWidth = Math.max(0, totalWidth - padding);
    setMaxNameWidth(nameWidth);
  }, []);

  const { ref: textRef } = useResizeObserver<HTMLDivElement>({
    onResize: calculateNameWidth,
  });

  const { data: cachedFilters, isLoading } = useQuery({
    queryKey: [GLOBAL_FILTER_KEY, globalFilter],
    queryFn: async () => {
      return getGlobalFilterItems(globalFilter);
    },
  });

  return (
    <>
      {open && !isLoading && (
        <Suspense>
          <GlobalFilterModal
            cachedFilters={cachedFilters ?? []}
            close={() => {
              setOpen(false);
            }}
          />
        </Suspense>
      )}
      <button
        ref={btnRef}
        type='button'
        onClick={() => setOpen(true)}
        className={cn(
          'flex w-full items-center justify-between rounded rounded-input border border-input bg-surface p-3 ring-offset-background transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden',
          {
            'border-primary bg-muted': !!cachedFilters?.length,
          }
        )}
      >
        <div className='flex flex-col text-start'>
          <FilterHeader currentFilters={cachedFilters ?? []} />
          <div
            style={{ maxWidth: maxNameWidth }}
            ref={textRef}
            className='mt-1 text-xs leading-none font-medium'
          >
            {isLoading ? (
              'Laddar...'
            ) : (
              <GlobalFilters currentFilters={cachedFilters ?? []} />
            )}
          </div>
        </div>

        <Icons.selectIndicator width={20} />
      </button>
    </>
  );
}

function GlobalFilters({
  currentFilters,
}: {
  currentFilters: GlobalFilterItem[];
}) {
  if (!currentFilters.length) {
    return 'Visar hela beståndet';
  }

  const str = `${currentFilters[0].displayString}`;

  const additional = currentFilters.length - 1;

  if (additional > 0) {
    return (
      <div className='flex flex-row items-center'>
        <div className='mr-1 truncate'>{str}</div>
        <Badge
          className='h-5 min-w-5 px-1 text-xs tabular-nums'
          variant='default'
        >
          +{additional}
        </Badge>
      </div>
    );
  }

  return str;
}

function FilterHeader({
  currentFilters,
}: {
  currentFilters: GlobalFilterItem[];
}) {
  if (!currentFilters.length)
    return <div className='text-xs leading-none'>Filtrera systemet</div>;

  const hasExternalClientFilters = currentFilters.find(
    (f) => f.type === 'filter_external_clients'
  );
  const hasSegmentFilters = currentFilters.find(
    (f) => f.type === 'global_segments'
  );
  const hasRealEstateFilters = currentFilters.find(
    (f) => f.type === 'global_realestates'
  );
  const hasCompanyFilters = currentFilters.find(
    (f) => f.type === 'global_companies'
  );

  const filterTypeCount = [
    hasExternalClientFilters,
    hasSegmentFilters,
    hasRealEstateFilters,
    hasCompanyFilters,
  ].filter((f) => f).length;

  return (
    <div className='text-xs leading-none'>
      {filterTypeCount === 3
        ? 'Uppdragsgivare, bolag & fastighet'
        : filterTypeCount === 2
          ? hasExternalClientFilters
            ? 'Uppdragsgivare & ' +
              (hasRealEstateFilters ? 'fastighet' : 'bolag')
            : 'Bolag & fastighet'
          : hasExternalClientFilters
            ? 'Uppdragsgivare'
            : hasSegmentFilters
              ? 'Segment'
              : hasRealEstateFilters
                ? 'Fastighet'
                : 'Bolag'}
    </div>
  );
}
